import { Injector } from '@angular/core';
import { Store } from '@sama/app-state';
import { assert, isDefined } from '@sama/js-core';
import { isEmpty } from 'lodash';
import { combineLatest, filter } from 'rxjs';
import { Plane, Vector3 } from 'three';
import { AbstractToolHelper } from '../../../cross-team-shared/annotation/shared/tool-helpers/abstract-tool-helper';
import { EventTrackingValue } from '../../../team-apis/core/event-tracking';
import {
  ShellConnector,
  shellConnectorToken,
} from '../../../team-apis/core/shell-connector';
import { isOutlinedSphere } from '../../render/mesh/outlined-sphere';
import { subpointRadius } from '../../render/mesh/poly-shape/abstract-polyshape-helper';
import { LineService } from '../../render/mesh/poly-shape/line/line.service';
import { PolygonService } from '../../render/mesh/poly-shape/polygon/polygon.service';
import { PointCloudContentStore } from '../../state/point-cloud-content-store';
import { RenderState } from '../../state/render-state';
import { pointToVector, vectorToPoint } from '../../utils/point-cloud-utils';
import { subtractPoints } from '../../utils/point-utils';
import { RayCastService } from '../../utils/ray-cast.service';
import { MeasureToolState } from './measure-tool';
import { calculateSubpointHeightDistance } from './measure-utils';

export class subpointHeightToolHelper extends AbstractToolHelper {
  private lineService: LineService;
  private polygonService: PolygonService;
  private shellConnector: ShellConnector;
  private state = MeasureToolState.Waiting;
  private store: PointCloudContentStore;
  private renderStore: Store<RenderState>;
  private rayCastService: RayCastService;
  private rayCastingPlane = new Plane();

  constructor(injector: Injector) {
    super();
    this.lineService = injector.get(LineService);
    this.polygonService = injector.get(PolygonService);
    this.shellConnector = injector.get(shellConnectorToken);
    this.store = injector.get(PointCloudContentStore);
    this.renderStore = this.store('render');
    this.rayCastService = injector.get(RayCastService);
  }

  override startListening() {
    this.renderStore('measurementMode').set('subpoint-height');
  }

  handleClick() {
    switch (this.state) {
      case MeasureToolState.Waiting:
        this.startMeasurement();
        break;
      case MeasureToolState.InProgress:
        this.endMeasurement();
        break;
      case MeasureToolState.Blocked:
        this.resetMeasure();
        break;
    }
  }

  protected override cleanUp() {
    super.cleanUp();
    this.renderStore('measurementMode').set(undefined);
    this.renderStore('subpointHeightMeasurement').set(undefined);
  }

  private subscribeToCameraUpdates() {
    this.subscribeTo(
      combineLatest([
        this.store('camera')('position').$,
        this.renderStore('subpointHeightMeasurement').$.pipe(filter(isDefined)),
      ]).pipe(filter(([_, { active }]) => active)),
      ([cameraPosition, { reference }]) => {
        const planeNormal = new Vector3()
          .fromArray(subtractPoints(reference, cameraPosition))
          .normalize();
        this.rayCastingPlane.setFromNormalAndCoplanarPoint(
          planeNormal,
          pointToVector(reference),
        );
      },
    );
  }

  private subscribeToContainerPoint() {
    this.subscribeTo(this.store('mouse')('containerPoint').$, () => {
      const { subpointHeightMeasurement } = this.store.state().render;
      if (subpointHeightMeasurement && subpointHeightMeasurement.active) {
        const intersectingPoint = this.rayCastService.getPlaneIntersectionPoint(
          this.rayCastingPlane,
        );

        this.renderStore('subpointHeightMeasurement').assign({
          target: intersectingPoint.toArray(),
        });
      }
    });
  }

  private startMeasurement() {
    const intersectedPosition = this.getIntersectedSubpointPosition();

    if (intersectedPosition) {
      this.configureCameraForSubpoint(intersectedPosition);

      this.store('render')('subpointHeightMeasurement').set({
        reference: vectorToPoint(intersectedPosition),
        target: vectorToPoint(intersectedPosition),
        active: true,
      });

      this.subscribeToCameraUpdates();
      this.subscribeToContainerPoint();

      this.state = MeasureToolState.InProgress;
      this.trackEvent({ source: 'start_measure' });
    }
  }

  private endMeasurement() {
    this.renderStore('subpointHeightMeasurement').assign({
      active: false,
    });

    this.state = MeasureToolState.Blocked;
    this.trackEvent({
      source: 'end_measure',
      distance_measured: calculateSubpointHeightDistance(this.store.state()),
    });
  }

  private resetMeasure() {
    void this.stopListening();
    this.trackEvent({ source: 'reset_measure' });
  }

  private configureCameraForSubpoint(subpointPosition: Vector3) {
    const newPosition = subpointPosition.toArray();
    newPosition[0] += subpointRadius * 30;

    this.store('camera').assign({
      position: newPosition,
      target: subpointPosition.toArray(),
    });
  }

  private trackEvent(eventContext: EventTrackingValue) {
    this.shellConnector.trackEvent({
      id: 'measurement_click',
      bucket: 'navigation',
      sub_bucket: 'frame',
      mode: 'subpoint-height',
      ...eventContext,
    });
  }

  private getIntersectedSubpointPosition() {
    const subPoints = [
      ...this.lineService.getSubPoints(),
      ...this.polygonService.getSubPoints(),
    ];

    const intersections = this.rayCastService.intersectObjects(subPoints);

    if (isEmpty(intersections)) {
      return undefined;
    }
    const sphere = intersections[0].object.parent;
    assert(
      sphere && isOutlinedSphere(sphere),
      'intersected parent object must be an outlined sphere',
    );

    return sphere.position;
  }
}
